package com.swetdot.swetgram.ui.chats

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.ChatRepository
import com.swetdot.swetgram.data.MuteDeadline
import com.swetdot.swetgram.data.MuteDuration
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.data.realtime.RealtimeClient
import com.swetdot.swetgram.data.realtime.RealtimeSignal
import com.swetdot.swetgram.domain.Chat
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.domain.User
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.ui.chat.TypingState
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.count
import com.swetdot.swetgram.ui.text.text
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.chatlist_error_archive_failed
import swetgram.shared.generated.resources.chatlist_error_delete_failed
import swetgram.shared.generated.resources.chatlist_error_mute_failed
import swetgram.shared.generated.resources.chatlist_error_offline
import swetgram.shared.generated.resources.chatlist_error_pin_failed
import swetgram.shared.generated.resources.chatlist_error_pin_limit
import swetgram.shared.generated.resources.chatlist_error_refresh_failed
import swetgram.shared.generated.resources.chatlist_error_restore_failed
import swetgram.shared.generated.resources.chatlist_error_search_failed
import swetgram.shared.generated.resources.chatlist_error_timeout
import swetgram.shared.generated.resources.chatlist_error_unmute_failed
import swetgram.shared.generated.resources.chatlist_error_unpin_failed
import swetgram.shared.generated.resources.chatlist_error_upgrade_required
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.filterIsInstance
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import kotlin.time.Clock

/**
 * The chat list.
 *
 * [chats] comes straight from the cache, so the screen paints on the first frame with whatever
 * was there last time. The refresh runs alongside and its failures are reported separately --
 * a dead network shows a message ABOVE the list, it never empties it.
 *
 * [archived] is a SECOND cache query rather than a filter over the first: `selectChats` takes the
 * archived flag in SQL, and SPS §10.6 only ever shows the two together as a count ("Archived, 3").
 * It is refreshed alongside the inbox because the Archived entry has to disappear when the last
 * archived chat is restored -- possibly on another device.
 *
 * [commands] is nullable so a preview or a test can construct this with nothing behind it; the
 * screen then hides the row actions rather than offering ones that cannot run.
 */
class ChatListViewModel(
    private val repository: ChatRepository,
    private val realtime: RealtimeClient,
    private val commands: ChatCommands? = null,
    /**
     * The signed-in user, used only to drop their OWN typing frames -- the server publishes them
     * to the whole conversation channel including the sender. Null in previews and tests, which
     * have no session; see [observeTyping].
     */
    private val me: UserId? = null,
) : ViewModel() {

    val chats: StateFlow<List<Chat>> = repository.observeChats(archived = false)
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptyList())

    val archived: StateFlow<List<Chat>> = repository.observeChats(archived = true)
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptyList())

    /**
     * When each muted conversation's mute lapses. Straight from the repository's session overlay --
     * see `ChatRepository.muteDeadlines` for why the deadline is not in the cache.
     *
     * A conversation missing from this map is not "unmuted": `Chat.isMuted` answers that. The two
     * are read together by the row menu, which shows the bell from the flag and the date from here,
     * and says only "Muted" when the flag is set and the date is not known yet.
     */
    val muteDeadlines: StateFlow<Map<String, MuteDeadline>> = repository.muteDeadlines

    /**
     * A refresh THE USER ASKED FOR is in flight -- the pull gesture, or a banner's Retry.
     *
     * NOT "a read is running", which is [inFlight]. The two were one flag, and that is the whole of
     * the reload the user reported: `ChatListScreen` hands this straight to `PullToRefreshBox`,
     * which drops a spinner over the list for as long as it is true, so every BACKGROUND read --
     * re-entering the tab, a message arriving in any conversation -- raised the same indicator a
     * pull does, on a list that already had every row on it and would come back identical.
     *
     * A cache-backed screen must not have to prove it is working. See [refreshQuietly].
     */
    private val _refreshing = MutableStateFlow(false)
    val refreshing: StateFlow<Boolean> = _refreshing.asStateFlow()

    /**
     * A `UiText` rather than a `String`, which is what makes any of this reach a Russian phone.
     *
     * The words are chosen HERE, in a view model, where `stringResource` cannot be called -- it is
     * a `@Composable` and this is not one. So the rule names the message and `ChatListScreen`
     * resolves it where it draws; see `ui/text/UiText.kt`. Nothing in `Navigation.kt` changed for
     * it, because the flow's type and the screen parameter's type moved together.
     */
    private val _error = MutableStateFlow<UiText?>(null)
    val error: StateFlow<UiText?> = _error.asStateFlow()

    /** True only when there is genuinely nothing to show AND nothing in flight. */
    private val _loadedOnce = MutableStateFlow(false)
    val loadedOnce: StateFlow<Boolean> = _loadedOnce.asStateFlow()

    /**
     * A short-lived sentence about the last row action -- the pin limit, a failed archive.
     *
     * Separate from [_error], which describes the LIST. A failed pin must not make the whole
     * screen look broken, and a refresh that succeeds afterwards must not silently clear the
     * explanation for why the pin did not happen.
     */
    private val _notice = MutableStateFlow<UiText?>(null)
    val notice: StateFlow<UiText?> = _notice.asStateFlow()

    // ------------------------------------------------------------------ search (SPS §10.4)

    private val _query = MutableStateFlow("")
    val query: StateFlow<String> = _query.asStateFlow()

    private val _people = MutableStateFlow<List<User>>(emptyList())
    val people: StateFlow<List<User>> = _people.asStateFlow()

    private val _searching = MutableStateFlow(false)
    val searching: StateFlow<Boolean> = _searching.asStateFlow()

    /** True once a query has produced an answer, so "no results" is not shown before one. */
    private val _searchSettled = MutableStateFlow(false)
    val searchSettled: StateFlow<Boolean> = _searchSettled.asStateFlow()

    /**
     * Why the last people lookup produced nothing, or null when it produced an ANSWER.
     *
     * Separate from [_error], which describes the inbox, and from [_notice], which describes a row
     * action. This one describes the SEARCH, and it exists because without it a failed lookup and
     * an empty one are byte-identical on screen: the screen falls through to SDS §17.2's "No
     * results found." and tells a user who is merely offline that the account does not exist.
     *
     * SDS §25.1 requires an error state to "explain what happened" and §29's checklist requires the
     * screen to have one at all. `NewChatViewModel` already reports exactly this failure -- the two
     * search boxes in the app must not disagree about whether it is worth mentioning.
     */
    private val _searchError = MutableStateFlow<UiText?>(null)
    val searchError: StateFlow<UiText?> = _searchError.asStateFlow()

    val canSearchPeople: Boolean get() = commands != null
    val canActOnRows: Boolean get() = commands != null

    private var searchJob: Job? = null
    private var searchGeneration: Long = 0

    init {
        // QUIETLY, because the first read already has a way to say it is happening: with nothing
        // cached the screen draws skeletons off `loadedOnce`, and with a warm cache it draws the
        // rows. A spinner on top of either is a second statement about the same read.
        refreshQuietly()
        // Every DURABLE event touches this screen: a new message changes a preview and an unread
        // count no matter which conversation it landed in, so unlike the chat screen nothing is
        // filtered by conversation here.
        //
        // Two things are dropped FROM THE REFRESH, and both have to be.
        //
        // Typing, because it is ephemeral -- there is nothing for a refresh to find -- and it
        // arrives per keystroke burst in every conversation the user belongs to, so refreshing on it
        // would turn somebody else's typing into a continuous reload of the whole chat list. It is
        // collected separately, below, where it costs nothing but a map entry.
        //
        // And read receipts, which are dropped for a reason of their own: they are about the OTHER
        // party's watermark, and nothing this screen renders moves when it does. The unread count
        // here is the caller's, cleared by their own reads -- and their own read has already
        // happened locally by the time any frame could arrive. So a receipt would buy a full
        // `GET /conversations` per tick turned somewhere else, for a list that would come back
        // identical. The chat screen folds them; this one has nothing to fold.
        viewModelScope.launch {
            realtime.signals
                .filter { it !is RealtimeSignal.Typing && it !is RealtimeSignal.Read }
                .collectLatest {
                    delay(REFRESH_DEBOUNCE_MS)
                    // QUIETLY. Nobody asked for this read -- somebody else sent a message -- and
                    // announcing it put a spinner over the inbox on every incoming message in every
                    // conversation, which on a busy account is most of the time the tab is open.
                    refreshQuietly()
                }
        }
        observeTyping()
    }

    // ------------------------------------------------------------------ typing (SDS §12.1)

    private val _typing = MutableStateFlow<Map<String, TypingState>>(emptyMap())

    /**
     * The conversations somebody else is currently typing in.
     *
     * A SET of ids rather than names: the chat list row has one preview line and the row is
     * already titled with whoever is in the conversation, so "typing…" there says everything a
     * name would. (The conversation header, which has the person's name in the title above it,
     * takes the same view -- see `typingLabel`.) That also keeps this independent of whether the
     * member list happens to be cached yet, which the socket does not wait for.
     *
     * Ticked rather than pushed, for the reason [TypingState] documents: entries expire by elapsed
     * time and nothing emits when one goes stale, so the state has to be ASKED. The ticker runs
     * only while somebody is actually typing and stops the moment the map empties -- a chat list
     * sitting idle in the background wakes up for nothing.
     */
    @OptIn(ExperimentalCoroutinesApi::class)
    val typingChatIds: StateFlow<Set<String>> = _typing
        .flatMapLatest { byChat ->
            flow {
                while (true) {
                    val now = Clock.System.now().toEpochMilliseconds()
                    val active = byChat
                        .filterValues { it.typistsAt(now).isNotEmpty() }
                        .keys
                    emit(active)
                    if (active.isEmpty()) break
                    delay(TYPING_TICK_MS)
                }
            }
        }
        .distinctUntilChanged()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptySet())

    /**
     * Folds typing frames in, per conversation.
     *
     * The signed-in user's own frames are dropped: the server publishes to the whole conversation
     * channel INCLUDING the sender, and a row that says "typing…" while you are the one typing is
     * worse than no indicator at all -- it is wrong about who is doing what.
     *
     * [me] is nullable because this view model is constructed in previews and tests without a
     * session. A null one cannot filter, which is the honest degradation: it would show your own
     * typing rather than crash.
     */
    private fun observeTyping() {
        viewModelScope.launch {
            realtime.signals
                .filterIsInstance<RealtimeSignal.Typing>()
                .filter { it.userId != me?.value }
                .collect { signal ->
                    val now = Clock.System.now().toEpochMilliseconds()
                    val current = _typing.value
                    val before = current[signal.conversationId] ?: TypingState.Empty
                    val after = before.onTyping(signal.userId, signal.isTyping, now)
                    // Identity, not equality: TypingState hands back THIS when nothing changed,
                    // and re-emitting the map would restart the expiry ticker on every repeat
                    // frame -- one every three seconds, per conversation, for as long as somebody
                    // keeps typing.
                    if (after === before) return@collect
                    _typing.value = current + (signal.conversationId to after)
                }
        }
    }

    /**
     * A read is in flight, whether or not anybody can see it.
     *
     * SEPARATE FROM [_refreshing], which is the flag the screen turns into a spinner. This one used
     * to be that one, so re-entrancy and visibility were the same fact and there was no way to run
     * a read without announcing it.
     */
    private var inFlight = false

    /**
     * Set when an event lands while a read is already in flight. Without it that event is simply
     * dropped -- and since the socket only notifies once per change, the list would then stay
     * stale until something else happened to arrive.
     */
    private var refreshAgain = false

    /**
     * The refresh the USER asked for: the pull gesture, and every banner's Retry.
     *
     * The indicator is the receipt for a gesture. That is the only thing it is for, and it is why
     * this and [refreshQuietly] are two entry points rather than one with a spinner nobody chose.
     */
    fun refresh() {
        read(announce = true)
    }

    /**
     * The same read, with nothing on screen to show for it.
     *
     * WHAT THE USER ACTUALLY REPORTED. Re-entering the Chats tab re-runs the inbox read -- it has
     * to, because marking a conversation read is a per-member write the server deliberately does
     * not announce, so nothing else clears a badge the user just read away (see `ui/Navigation.kt`).
     * That read is CORRECT and stays. What was wrong is that it went through [refresh] and so
     * raised the pull-to-refresh spinner, on a list whose every row was already on screen and which
     * came back identical. The cache is painted first and always; the network catches up underneath
     * it, which is the whole promise `ChatRepository`'s header makes.
     */
    fun refreshQuietly() {
        read(announce = false)
    }

    private fun read(announce: Boolean) {
        // Set BEFORE the in-flight test, so a pull during a background read still gets its
        // indicator: the read the gesture asked for is genuinely running, and the `finally` below
        // takes the indicator down when it settles.
        if (announce) _refreshing.value = true
        if (inFlight) {
            refreshAgain = true
            return
        }
        inFlight = true
        _error.value = null
        viewModelScope.launch {
            try {
                repository.refresh(archived = false)
                // Second, and deliberately not in parallel: it is the less important of the two,
                // and a failure here must not be what the user is told about their inbox. Its own
                // failure is swallowed -- the Archived entry simply does not appear.
                runCatching { repository.refresh(archived = true) }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (e: Throwable) {
                // THROWABLE, not ApiError. `ChatRepository.refresh` can also throw a
                // SerializationException -- `ApiClient` decodes the body OUTSIDE its try/catch
                // ladder, so a `ConversationPage` this build cannot parse arrives raw -- and any
                // SQLite failure out of its `queries.transaction { }` block. Neither is an
                // ApiError, so both used to leave `_error` null while `finally` still set
                // `loadedOnce`, and the screen then painted "No chats yet." over a read that
                // failed. Every neighbouring view model (ui/profile, ui/groups, ui/discover,
                // ChatViewModel) already catches Throwable for this reason; this file was the one
                // not brought along.
                _error.value = if (e is ApiError) {
                    listMessage(e)
                } else {
                    Res.string.chatlist_error_refresh_failed.text()
                }
            } finally {
                inFlight = false
                _loadedOnce.value = true
                if (refreshAgain) {
                    refreshAgain = false
                    // The visibility CARRIES FORWARD rather than being re-decided: an indicator the
                    // user pulled up must not disappear while the read it stands for is being run
                    // again, and a queued background read must not inherit a spinner.
                    read(announce = _refreshing.value)
                } else {
                    _refreshing.value = false
                }
            }
        }
    }

    // ------------------------------------------------------------------ search

    /**
     * Types into the search bar.
     *
     * Existing chats are filtered by the SCREEN, from the list it already has, so those results
     * appear on the same frame as the keystroke and work with no network at all. Only the people
     * lookup goes to the server, and only after the typing pauses: SDS §11.3 says results update
     * as the user types, and DECISIONS §12's 60-requests-per-minute budget says not once per
     * character.
     */
    fun onQueryChange(next: String) {
        val generation = ++searchGeneration
        _query.value = next
        searchJob?.cancel()
        // The previous verdict was about a query that is no longer on screen. Keeping it would
        // put "No connection" over results that have since arrived, which is the same class of
        // mistake `staleAwareError` closes on the auth forms.
        _searchError.value = null

        val trimmed = next.trim()
        if (trimmed.isEmpty()) {
            _people.value = emptyList()
            _searching.value = false
            _searchSettled.value = false
            return
        }

        val client = commands
        if (client == null) {
            // No people search in this build; the chat filter above still works.
            _searchSettled.value = true
            return
        }

        _searching.value = true
        searchJob = viewModelScope.launch {
            delay(SEARCH_DEBOUNCE_MS)
            try {
                // The leading @ of SPS §14.2 is a UI convention, not part of the username the
                // server indexes -- sending it would match nothing.
                val people = client.searchPeople(trimmed.removePrefix("@"))
                if (generation != searchGeneration) return@launch
                _people.value = people
                settle()
            } catch (cancellation: CancellationException) {
                // NOT settled, and deliberately not in a `finally`. This job is cancelled by the
                // NEXT keystroke, which has already set `searching = true` for its own request --
                // so a `finally` here ran afterwards and reported the newer query as answered,
                // with the older query's empty list. For the one frame before the new request
                // returned, the screen said "No results found." about a search still in flight.
                throw cancellation
            } catch (error: Throwable) {
                if (generation != searchGeneration) return@launch
                // NAMED, not swallowed. This used to be silent, on the argument that the chat
                // matches beside it are still correct -- but when there are none the screen fell
                // through to "No results found. Try another name or username.", and a user who is
                // simply offline or over DECISIONS §12's 60-requests-per-minute budget was told,
                // in the design system's own words, that no such account exists. That is a verdict
                // the client has no evidence for, on the primary path to starting a conversation.
                _people.value = emptyList()
                _searchError.value = searchFailureMessage(error)
                settle()
            }
        }
    }

    /** This query has produced an answer -- of some kind. Both non-cancellation paths end here. */
    private fun settle() {
        _searching.value = false
        _searchSettled.value = true
    }

    /** Runs the current query again. The banner's Retry, for a failure the user can act on. */
    fun retrySearch() {
        onQueryChange(_query.value)
    }

    fun clearQuery() {
        onQueryChange("")
    }

    // ------------------------------------------------------------------ row actions

    /** SPS §10.5. A sixth pin answers `409`, which is a limit message rather than a failure. */
    fun setPinned(id: ChatId, pinned: Boolean) {
        val client = commands ?: return
        act(failure = pinFailure(pinned), onConflict = pinLimitMessage()) {
            if (pinned) client.pin(id) else client.unpin(id)
        }
    }

    /** SPS §10.6. Reversible -- the Archived entry is where it comes back from. */
    fun setArchived(id: ChatId, archived: Boolean) {
        val client = commands ?: return
        act(failure = archiveFailure(archived)) {
            if (archived) client.archive(id) else client.unarchive(id)
        }
    }

    /**
     * SPS §15.3's "mute a specific chat", for one of the contract's five durations.
     *
     * A null [duration] unmutes. One entry point rather than two because the row offers exactly one
     * control whose meaning flips with `chat.isMuted`, and splitting it would give the screen a
     * second way to get the state wrong.
     *
     * Runs through [act] like every other row action, so the list is re-read afterwards and the
     * bell of SDS §12.1 appears (or goes) without this view model guessing at the new state. The
     * DEADLINE does not wait for that refresh -- `ChatRepository.mute` folds the server's own
     * answer into [muteDeadlines] before returning.
     *
     * No confirmation, deliberately. SDS §12.2 requires one for deletion and for nothing else, and
     * a mute is reversed from the same menu entry that set it.
     */
    fun setMuted(id: ChatId, duration: MuteDuration?) {
        act(failure = muteFailure(muted = duration != null)) {
            if (duration != null) repository.mute(id, duration) else repository.unmute(id)
        }
    }

    /**
     * SPS §10.7. For this user only; the screen confirms first.
     *
     * THE CACHE IS TOLD SEPARATELY, and nothing else in this file has to. Pin, archive and mute all
     * change a column the refresh afterwards writes back; a deletion removes the conversation from
     * `listConversations` entirely, and `ChatRepository.refresh` only ever upserts what the page
     * contains -- so the row survived its own deletion and the screen kept drawing it. See
     * [ChatRepository.forget], which is the half that was missing.
     *
     * That stale row is also where the reported failure came from. Deleting it again reaches a
     * conversation the server no longer considers the caller a member of, which answers
     * `404 conversation_not_found` -- so the second attempt onwards reported a failure for a chat
     * that was already deleted, for ever. A 404 here is therefore treated as the deletion having
     * COMPLETED: see [deletionAlreadyDone]. Without that, every install already holding a stranded
     * row would keep it after this fix.
     */
    fun deleteChat(id: ChatId) {
        val client = commands ?: return
        act(failure = Res.string.chatlist_error_delete_failed.text()) {
            try {
                client.deleteForMe(id)
            } catch (error: Throwable) {
                if (!deletionAlreadyDone(error)) throw error
            }
            repository.forget(id)
        }
    }

    /**
     * Runs a row action and re-reads the list.
     *
     * Not optimistic. Every one of these reorders or removes a row, and guessing the new order
     * locally then correcting it a moment later is a visible jump under the user's finger -- the
     * refresh is one request and the row was already tapped, so the wait is one the user expects.
     */
    private fun act(
        failure: UiText,
        onConflict: UiText? = null,
        block: suspend () -> Unit,
    ) {
        _notice.value = null
        viewModelScope.launch {
            // TWO REQUESTS, TWO SENTENCES, AND THEY MUST NOT SHARE A `catch`.
            //
            // This was one `try` around the action AND the re-read, and that single block is what
            // produced the defect the user reported: a delete that FAILED said "Could not refresh",
            // because every `ApiError` went to the list's ladder.
            //
            // Fixing only the ladder swapped one false sentence for another, in the opposite
            // direction, and that is worth spelling out because it is the trap here. The action
            // succeeds, the re-read that follows it answers 500, the one `catch` sees an `ApiError`
            // and now reports the ACTION's sentence -- "Could not delete that chat." -- about a chat
            // that has just been deleted and, since `deleteChat` prunes the cache, has already
            // vanished from the screen the sentence is printed on.
            //
            // So the action is attributed with [rowActionMessage] and the re-read with
            // [listMessage], each in its own `try`. A read that fails after a successful action is
            // still worth saying -- the list is now stale -- but it is a statement about the list.
            try {
                block()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _notice.value = rowActionNotice(RowActionPhase.Action, error, failure, onConflict)
                // The action did not happen. Re-reading would cost a request to confirm that
                // nothing changed, and could then overwrite this sentence with its own.
                return@launch
            }

            try {
                repository.refresh(archived = false)
                runCatching { repository.refresh(archived = true) }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _notice.value = rowActionNotice(RowActionPhase.Reread, error, failure, onConflict)
            }
        }
    }

    fun dismissNotice() {
        _notice.value = null
    }

    private companion object {
        const val REFRESH_DEBOUNCE_MS = 150L

        /**
         * Long enough that a word typed at speed is one request, short enough that the results
         * feel like they are keeping up.
         */
        const val SEARCH_DEBOUNCE_MS = 250L

        /**
         * How often [typingChatIds] re-evaluates while somebody is typing. Well under
         * `TYPING_TTL_MS`, so a row stops claiming somebody is typing promptly once the frames
         * stop, and coarse enough to cost one wake-up per second only while it is true.
         */
        const val TYPING_TICK_MS = 1_000L
    }
}

// ---------------------------------------------------------------------------------------------
// What a failure says. Pure rules: they choose a MESSAGE, never words.
// ---------------------------------------------------------------------------------------------

/**
 * SDS §18.2 and §25.1: what a failed PEOPLE SEARCH says.
 *
 * ONE FUNCTION NOW, WHERE THERE WERE TWO. `NewChatScreen.kt` carried a private `searchMessage` with
 * these exact two branches, kept identical by hand and by a pair of KDocs explaining that they had
 * to be -- because the complaint that produced them was that the app's two search boxes disagreed
 * about a failure. That duplication survived only because the two files belonged to different areas
 * and neither could change the other's `String`; both are localised together now, so the copy of it
 * is gone and the New Chat screen calls this.
 *
 * The 429 oddity is unchanged and still recorded at `chatlist_error_refresh_failed`: an unmapped
 * `ApiError` over a people lookup says "Could not refresh". It is now ONE sentence in one resource
 * rather than two literals in two files, which is the part this pass could actually fix.
 */
internal fun searchFailureMessage(error: Throwable): UiText = when (error) {
    is ApiError -> listMessage(error)
    else -> Res.string.chatlist_error_search_failed.text()
}

/**
 * SDS §18.2: say what happened, never the raw server message.
 *
 * Reached from three places -- the inbox refresh, both search boxes and the New Chat screen's
 * refused open -- so it is the single most-rendered failure rule in this package, and the one whose
 * keys `failuresCoincide` depends on: two requests that fail the same way must resolve the same key,
 * or the screen draws the same sentence twice.
 *
 * ROW ACTIONS ARE NO LONGER AMONG THEM; they use [rowActionMessage]. `chatlist_error_refresh_failed`
 * is this rule's catch-all and it says "Could not refresh", which is true of a failed read of the
 * list and of nothing else.
 */
internal fun listMessage(error: ApiError): UiText = when (error) {
    is ApiError.Offline -> Res.string.chatlist_error_offline.text()
    is ApiError.Timeout -> Res.string.chatlist_error_timeout.text()
    is ApiError.UpgradeRequired -> Res.string.chatlist_error_upgrade_required.text()
    else -> Res.string.chatlist_error_refresh_failed.text()
}

/**
 * SDS §18.2: what a failed ROW ACTION says -- a pin, an archive, a mute, a delete.
 *
 * SEPARATE FROM [listMessage] BECAUSE THE SUBJECT IS DIFFERENT, and conflating the two is what put
 * "Could not refresh" on a failed delete. `act` sent every `ApiError` to the list's ladder, whose
 * catch-all is `chatlist_error_refresh_failed`; the caller's own sentence was reached only by errors
 * that were not `ApiError` at all, so `chatlist_error_delete_failed` was unreachable for exactly the
 * case it exists for. The user was told the app could not refresh, about a request that refreshed
 * nothing, while the row they had asked to delete stayed where it was.
 *
 * The three branches kept from [listMessage] are the ones that describe the CONNECTION rather than
 * the inbox -- no network, no answer in time, a build the server will not serve -- and each of those
 * is a truer statement about a failed pin than "Could not pin that chat." would be. Everything else
 * is a refusal OF THIS ACTION, and only the caller knows which action that was, so it passes
 * [failure] through.
 *
 * [onConflict] stays ahead of all of them: SPS §10.5's sixth pin is a limit, not a failure.
 */
internal fun rowActionMessage(
    error: Throwable,
    failure: UiText,
    onConflict: UiText? = null,
): UiText = when {
    error is ApiError.Conflict && onConflict != null -> onConflict
    error is ApiError.Offline -> Res.string.chatlist_error_offline.text()
    error is ApiError.Timeout -> Res.string.chatlist_error_timeout.text()
    error is ApiError.UpgradeRequired -> Res.string.chatlist_error_upgrade_required.text()
    else -> failure
}

/** Which of a row action's two requests failed. See [rowActionNotice]. */
internal enum class RowActionPhase {
    /** The pin, archive, mute or delete itself. */
    Action,

    /** The re-read of the list that follows a SUCCESSFUL action. */
    Reread,
}

/**
 * What a row action says, given WHICH OF ITS TWO REQUESTS failed.
 *
 * A row action is two requests, not one: the action, and then the read that shows its result. They
 * fail for different reasons and they are not the same sentence, and BOTH WRONG ANSWERS HAVE NOW
 * SHIPPED, in opposite directions, which is why this is a rule and not an inline `when`.
 *
 *  - Originally `act` wrapped both in one `try` and sent every `ApiError` to [listMessage]. A
 *    delete that the server REFUSED said "Could not refresh" -- a sentence about the inbox, about a
 *    request that refreshed nothing -- and the row stayed where it was. That is the defect the user
 *    reported.
 *  - Repointing that one `catch` at [rowActionMessage] fixed the refusal case and broke the other
 *    one: a delete that SUCCEEDED, followed by a re-read that answered 500, then said "Could not
 *    delete that chat." about a chat that was already gone from the server and, because the cache
 *    is now pruned, gone from the screen the sentence was printed on.
 *
 * So the phase is an argument. [Action] failures are the action's own business and go through
 * [rowActionMessage]; [Reread] failures are a statement about the LIST, which is stale now, and go
 * through [listMessage].
 *
 * WHAT THIS RULE CANNOT DO, said plainly rather than left to be discovered. It decides the sentence
 * once somebody has told it which request failed -- it cannot check that `act` still calls it with
 * the right phase, because that is control flow inside a `ViewModel`. There is no test for that:
 * `ChatListViewModel` needs a `ChatRepository`, which needs a `SwetgramCache`, whose `SqlDriver` has
 * only android and ios actuals -- the same wall `ChatViewModelTest` documents at the top of its own
 * file. If the two `try` blocks in [act] are ever merged back into one, every test below stays
 * green and the defect returns.
 */
internal fun rowActionNotice(
    phase: RowActionPhase,
    error: Throwable,
    failure: UiText,
    onConflict: UiText? = null,
): UiText = when (phase) {
    RowActionPhase.Action -> rowActionMessage(error, failure, onConflict)
    RowActionPhase.Reread ->
        if (error is ApiError) listMessage(error) else Res.string.chatlist_error_refresh_failed.text()
}

/**
 * Did `deleteConversation` leave the conversation deleted, whatever it answered?
 *
 * A `404` says yes. `conversation_not_found` is the only 404 the operation declares and the server
 * raises it from a membership lookup that excludes rows already marked `deleted_for_self_at` -- so
 * on a conversation this screen is drawing, it means the deletion the user is asking for has already
 * happened. Calling that a failure is what made the defect permanent rather than merely visible:
 * the cache kept the row, so every attempt after the first hit this 404 and the row could never be
 * removed by any number of taps.
 *
 * Deliberately NOT extended to `403`. That is the contract's answer for a scope this caller may not
 * use (`everyone` on a group), which is a genuine refusal with the conversation still in place.
 */
internal fun deletionAlreadyDone(error: Throwable): Boolean = error is ApiError.NotFound

/**
 * SPS §10.5 and the contract's own `pinConversation` description.
 *
 * File-level and `internal` rather than a private companion constant, because [pinLimitMessage]
 * takes it as a default and a test has to be able to name the number the sentence will contain.
 */
internal const val MAX_PINNED_CHATS = 5

/**
 * The `409` from a sixth pin, as a PLURAL over the limit.
 *
 * A plural for a number that is a compile-time constant looks like ceremony and is not: the value
 * governs a Russian noun, so a limit of 1 or 2 would need `чат` or `чата` where a hand-written
 * `чатов` sat, and nothing anywhere would fail -- the sentence would simply be wrong, on one
 * language, for as long as nobody Russian looked at it. `if (limit == 1)` is not available for the
 * same reason it is never available: 21 is `one` in Russian and 11 is `many`.
 */
internal fun pinLimitMessage(limit: Int = MAX_PINNED_CHATS): UiText =
    Res.plurals.chatlist_error_pin_limit.count(limit)

/**
 * The three row actions whose refusal depends on WHICH WAY the action was going.
 *
 * Written out as one key per direction rather than one sentence with the verb substituted in.
 * "Could not %1$s that chat." reads fine in English and is unbuildable in Russian, where the two
 * verbs are not interchangeable tokens and the sentence around them is not the same shape.
 *
 * Lifted out of the view model so they can be asserted at all: `ChatListViewModel` takes a concrete
 * `ChatRepository`, `RealtimeClient` and `ChatCommands`, none of them substitutable in commonTest
 * (`ChatListSearchStateTest` records the same constraint about `searchDisplay`). A swapped pair
 * here -- Restore's sentence on Archive's failure -- is invisible to review and obvious to a user.
 */
internal fun pinFailure(pinned: Boolean): UiText =
    if (pinned) {
        Res.string.chatlist_error_pin_failed.text()
    } else {
        Res.string.chatlist_error_unpin_failed.text()
    }

internal fun archiveFailure(archived: Boolean): UiText =
    if (archived) {
        Res.string.chatlist_error_archive_failed.text()
    } else {
        Res.string.chatlist_error_restore_failed.text()
    }

internal fun muteFailure(muted: Boolean): UiText =
    if (muted) {
        Res.string.chatlist_error_mute_failed.text()
    } else {
        Res.string.chatlist_error_unmute_failed.text()
    }
